import {
  AiSkillScope,
  type SkillTemplate,
} from '@seen/core/models/skillTemplate'
import { orderBy } from 'lodash-es'
import { useMemo } from 'react'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useCurrentPractitionerPerms } from '#src/hooks/useCurrentPractitionerPerms.ts'

/**
 * Filters AI skills down to the ones the current practitioner may use, sorted
 * personal-first then alphabetically. Tech reviewers see every skill
 * regardless of scope or authorship.
 *
 * @param skills - The skills to filter, or `undefined` while they load.
 * @returns The visible skills, sorted personal-first then alphabetically.
 */
export function useVisibleSkills(
  skills: SkillTemplate[] | undefined
): SkillTemplate[] {
  const currentPractitioner = useCurrentPractitioner()
  const { isTech } = useCurrentPractitionerPerms()
  const currentReference = currentPractitioner.reference.reference

  return useMemo(
    () =>
      orderBy(
        (skills ?? []).filter(
          (skill) =>
            isTech || skill.canBeUsedBy({ reference: currentReference })
        ),
        [
          (skill) => (skill.scope === AiSkillScope.Personal ? 0 : 1),
          (skill) => skill.title.toLowerCase(),
        ],
        ['asc', 'asc']
      ),
    [currentReference, isTech, skills]
  )
}
